import { z } from 'zod';

export const EnvironmentSchema = z.enum(['development', 'production', 'test']);
export type Environment = z.infer<typeof EnvironmentSchema>;

export interface BaseConfig {
	environment: Environment;
	debug?: boolean;
}

export function loadEnvironment(): Environment {
	const env = process.env.NODE_ENV || 'development';
	return EnvironmentSchema.parse(env);
}

export function isProduction(): boolean {
	return loadEnvironment() === 'production';
}

export function isDevelopment(): boolean {
	return loadEnvironment() === 'development';
}

export function getRequiredEnv(key: string): string {
	const value = process.env[key];
	if (!value) {
		throw new Error(`Missing required environment variable: ${key}`);
	}
	return value;
}

export function getOptionalEnv(key: string, defaultValue?: string): string | undefined {
	return process.env[key] || defaultValue;
}

export class ConfigLoader<T> {
	constructor(private schema: z.ZodSchema<T>) {}

	load(config: Record<string, any>): T {
		try {
			return this.schema.parse(config);
		} catch (error) {
			if (error instanceof z.ZodError) {
				const issues = error.issues
					.map((issue) => `${issue.path.join('.')}: ${issue.message}`)
					.join(', ');
				throw new Error(`Configuration validation failed: ${issues}`);
			}
			throw error;
		}
	}

	loadFromEnv(envMapping: Record<keyof T, string>): T {
		const config: Record<string, any> = {};

		for (const [key, envKey] of Object.entries(envMapping)) {
			const value = process.env[envKey as string];
			if (value !== undefined) {
				config[key] = value;
			}
		}

		return this.load(config);
	}
}
